/* ═══════════════════════════════════════════════════════════════
   Editor Components — DocSettings, Library, Formatter, Settings
   Depends on: constants.ts, utils.ts
   ═══════════════════════════════════════════════════════════════ */

import React from 'react';
import {
  Upload,
  Plus,
  Copy,
  Trash2,
  X,
  Check,
  Play,
  CheckCircle2,
  XCircle,
  AlertTriangle,
  FileText,
  FolderOpen,
  Loader2,
  History,
} from 'lucide-react';
import { FONTS } from '../constants';
import { tokLabel, tokPreview, tokModStyle } from '../utils';
import { isWeb } from '../lib/platform';
import { AuthService } from '../services/authService';
import { SearchableSelect as SearchableSelectKV } from './SearchableSelect';
import { Input } from './ui/input';
import { Switch } from './ui/switch';
import { Button } from './ui/button';
import { Hint } from './ui/tooltip';
import { FORMA_TIPS } from '../formaTooltips';
import { DataTable, Column, GroupOption } from './DataTable';

const { useState: eUseState, useEffect: eUseEffect, useRef: eUseRef } = React;

/* ─── Searchable Single-Select Dropdown ─── */
function SearchableSelect({
  value,
  options,
  placeholder,
  onSelect,
  inputStyle,
}: {
  value: string;
  options: string[];
  placeholder?: string;
  onSelect: (v: string) => void;
  inputStyle?: React.CSSProperties;
}) {
  const [open, setOpen] = eUseState(false);
  const [query, setQuery] = eUseState('');
  const ref = eUseRef<HTMLDivElement>(null);

  eUseEffect(() => {
    if (!open) return;
    const handleOutside = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handleOutside);
    return () => document.removeEventListener('mousedown', handleOutside);
  }, [open]);

  const filtered = query
    ? options.filter((o) => o.toLowerCase().includes(query.toLowerCase()))
    : options;

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <div
        onClick={() => {
          setOpen(!open);
          setQuery('');
        }}
        style={{
          ...inputStyle,
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          minHeight: 32,
        }}
      >
        <span
          style={{
            flex: 1,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            color: value ? 'var(--tx)' : 'var(--tm)',
          }}
        >
          {value || '— Select client —'}
        </span>
        <span style={{ fontSize: 11, color: 'var(--tm)', marginLeft: 4, flexShrink: 0 }}>
          {open ? '▲' : '▼'}
        </span>
      </div>
      {open && (
        <div
          style={{
            position: 'absolute',
            top: '100%',
            left: 0,
            right: 0,
            zIndex: 200,
            marginTop: 2,
            background: 'var(--sf)',
            border: '1px solid var(--bd)',
            borderRadius: 6,
            boxShadow: '0 8px 24px rgba(0,0,0,.15)',
            maxHeight: 220,
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <div style={{ padding: '6px 8px', borderBottom: '1px solid var(--bd)' }}>
            <input
              autoFocus
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={placeholder || 'Search...'}
              onMouseDown={(e) => e.stopPropagation()}
              style={{
                width: '100%',
                padding: '5px 8px',
                borderRadius: 4,
                border: '1px solid var(--bd)',
                background: 'var(--ib)',
                color: 'var(--tx)',
                fontSize: 12,
                outline: 'none',
              }}
            />
          </div>
          <div style={{ overflow: 'auto', flex: 1 }}>
            {value && (
              <div
                onClick={() => {
                  onSelect('');
                  setOpen(false);
                }}
                style={{
                  padding: '7px 12px',
                  fontSize: 12,
                  color: 'var(--tm)',
                  cursor: 'pointer',
                  fontStyle: 'italic',
                  borderBottom: '1px solid var(--bd)',
                }}
                onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--sa)')}
                onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
              >
                Clear selection
              </div>
            )}
            {filtered.length === 0 && (
              <div
                style={{ padding: '12px', fontSize: 12, color: 'var(--tm)', textAlign: 'center' }}
              >
                No matches
              </div>
            )}
            {filtered.map((o) => (
              <div
                key={o}
                onClick={() => {
                  onSelect(o);
                  setOpen(false);
                }}
                style={{
                  padding: '7px 12px',
                  fontSize: 12,
                  cursor: 'pointer',
                  fontWeight: o === value ? 600 : 400,
                  color: o === value ? 'var(--ac)' : 'var(--tx)',
                  background: o === value ? 'rgba(var(--acr),0.06)' : 'transparent',
                }}
                onMouseEnter={(e) => {
                  if (o !== value) e.currentTarget.style.background = 'var(--sa)';
                }}
                onMouseLeave={(e) => {
                  if (o !== value) e.currentTarget.style.background = 'transparent';
                }}
              >
                {o}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

/* ─── Doc Settings Panel ─── */
/* ─── Doc Settings building blocks ─── */

const DS_LABEL: React.CSSProperties = {
  fontSize: 11,
  fontWeight: 600,
  color: 'var(--tm)',
  letterSpacing: '0',
  display: 'block',
  marginBottom: 6,
};
const DS_INPUT: React.CSSProperties = {
  width: '100%',
  height: 36,
  padding: '0 12px',
  borderRadius: 6,
  border: '1px solid var(--bd)',
  background: 'var(--sf)',
  color: 'var(--tx)',
  fontSize: 13,
  boxShadow: 'var(--shadow-sm)',
  boxSizing: 'border-box',
};

/** Settings group — "C hybrid": warm, soft, grouped (label above a rounded,
 *  borderless card) but every control stays inline-editable, no Apple-Settings
 *  drill-in. Reads like a modern canvas tool (Framer/Canva), not a form dump. */
function DSSection({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div>
      <div className="mb-2.5 select-none pl-0.5 text-[10px] font-bold uppercase tracking-[0.06em] text-muted">
        {title}
      </div>
      <div className="sf-ds-card flex flex-col gap-3.5 rounded-lg border border-border bg-surface p-4 shadow-sm">
        {children}
      </div>
    </div>
  );
}

/** A settings label that explains itself on hover/focus (DESIGN.md §7: the
 *  label stays a real <label>, the tooltip only adds description). */
function DSLabel({
  hint,
  htmlFor,
  children,
}: {
  hint?: string;
  htmlFor?: string;
  children: React.ReactNode;
}) {
  return (
    <Hint text={hint}>
      <label style={DS_LABEL} htmlFor={htmlFor} tabIndex={hint ? 0 : undefined}>
        {children}
      </label>
    </Hint>
  );
}

/** Compact toggle switch row. */
function DSToggle({
  on,
  label,
  hint,
  onToggle,
}: {
  on: boolean;
  label: string;
  hint?: string;
  onToggle: () => void;
}) {
  const id = React.useId();
  return (
    <div className="flex items-center justify-between gap-2 text-[12px]">
      <Hint text={hint}>
        <label htmlFor={id} className="cursor-pointer select-none">
          {label}
        </label>
      </Hint>
      <Switch id={id} checked={on} onCheckedChange={() => onToggle()} />
    </div>
  );
}

/** One margin field in the page diagram. */
function DSMarginInput({
  label,
  value,
  hint,
  onChange,
}: {
  label: string;
  value: number;
  hint?: string;
  onChange: (v: number) => void;
}) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
      <Hint text={hint}>
        <span
          tabIndex={hint ? 0 : undefined}
          style={{
            fontSize: 9,
            fontWeight: 700,
            color: 'var(--tm)',
            textTransform: 'uppercase',
            letterSpacing: '.05em',
          }}
        >
          {label}
        </span>
      </Hint>
      <Input
        type="number"
        step=".1"
        min="0"
        max="3"
        value={value}
        onChange={(e) => onChange(Number(e.target.value) || 0)}
        className="h-8 w-[52px] px-1 text-center font-mono text-[11px]"
      />
    </div>
  );
}

function DocSettings({
  doc,
  onUpdate,
  organizations,
  sections,
  pageVariant,
  onPageVariant,
}: {
  doc: any;
  onUpdate: (d: any) => void;
  organizations?: any[];
  /** When set, only render these DSSection titles (used to split Doc Settings
   *  across separate dockable tool palettes). Omit to render all. */
  sections?: string[];
  /** Which page variant the canvas is editing (first/odd/even) — the switcher
   *  renders inside Layout Options, next to the toggles that create variants. */
  pageVariant?: string;
  onPageVariant?: (v: string) => void;
}) {
  const u = (k: string, v: any) => onUpdate({ ...doc, [k]: v });
  const show = (t: string) => !sections || sections.includes(t);
  const pageVariants: string[] = [
    ...(doc.firstPageDifferent ? ['first'] : []),
    'odd',
    ...(doc.oddEvenDifferent ? ['even'] : []),
  ];

  const orgNames = organizations
    ? [...new Set(organizations.map((o) => o.name || o.shortName).filter(Boolean))]
    : [];

  return (
    <div
      className="sf-palette-flow"
      style={{ padding: 16, display: 'flex', flexDirection: 'column', gap: 18 }}
    >
      {show('Identity') && (
        <DSSection title="Identity">
          <div>
            <DSLabel hint={FORMA_TIPS.formaName}>Forma Name</DSLabel>
            <Input value={doc.standardName} onChange={(e) => u('standardName', e.target.value)} />
          </div>
          <div>
            <DSLabel hint={FORMA_TIPS.formaClient}>Client</DSLabel>
            <SearchableSelect
              value={doc.clientName || ''}
              options={orgNames}
              placeholder="Search clients..."
              onSelect={(v) => u('clientName', v)}
              inputStyle={DS_INPUT}
            />
          </div>
        </DSSection>
      )}

      {show('Typography') && (
        <DSSection title="Typography">
          <div>
            <DSLabel hint={FORMA_TIPS.bodyFont}>Body Font</DSLabel>
            <SearchableSelectKV
              value={doc.bodyFont}
              options={FONTS.map((f) => ({ value: f, label: f }))}
              onChange={(v) => u('bodyFont', v)}
              placeholder="— Font —"
              searchPlaceholder="Search fonts…"
              clearable={false}
              style={DS_INPUT}
            />
          </div>
          <div style={{ display: 'flex', gap: 10 }}>
            <div style={{ flex: 1 }}>
              <DSLabel hint={FORMA_TIPS.bodyFontSize}>Size (pt)</DSLabel>
              <Input
                type="number"
                min="6"
                max="16"
                value={doc.bodyFontSize}
                onChange={(e) => u('bodyFontSize', Number(e.target.value) || 10)}
                className="text-center font-mono"
              />
            </div>
            <div style={{ flex: 1 }}>
              <DSLabel hint={FORMA_TIPS.lineSpacing}>Line Spacing</DSLabel>
              <Input
                type="number"
                min="1"
                max="3"
                step=".5"
                value={doc.lineSpacing}
                onChange={(e) => u('lineSpacing', Number(e.target.value) || 1)}
                className="text-center font-mono"
              />
            </div>
          </div>
        </DSSection>
      )}

      {show('Page Margins (inches)') && (
        <DSSection title="Page Margins (inches)">
          {/* Page diagram — inputs sit where the margins live */}
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: '1fr auto 1fr',
              gridTemplateRows: 'auto auto auto',
              gap: 6,
              alignItems: 'center',
              justifyItems: 'center',
            }}
          >
            <div />
            <DSMarginInput
              label="Top"
              hint={FORMA_TIPS.marginTop}
              value={doc.marginTop}
              onChange={(v) => u('marginTop', v)}
            />
            <div />
            <DSMarginInput
              label="Left"
              hint={FORMA_TIPS.marginLeft}
              value={doc.marginLeft}
              onChange={(v) => u('marginLeft', v)}
            />
            <div
              style={{
                width: 58,
                height: 74,
                borderRadius: 3,
                border: '1px solid var(--bd)',
                background: 'var(--ib)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: '0 1px 4px rgba(0,0,0,.08)',
              }}
            >
              <div
                style={{
                  width: 36,
                  height: 52,
                  border: '1px dashed var(--bd)',
                  borderRadius: 2,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: 8,
                  color: 'var(--tm)',
                  textAlign: 'center',
                  lineHeight: 1.2,
                }}
              >
                body
              </div>
            </div>
            <DSMarginInput
              label="Right"
              hint={FORMA_TIPS.marginRight}
              value={doc.marginRight}
              onChange={(v) => u('marginRight', v)}
            />
            <div />
            <DSMarginInput
              label="Bottom"
              hint={FORMA_TIPS.marginBottom}
              value={doc.marginBottom}
              onChange={(v) => u('marginBottom', v)}
            />
            <div />
          </div>
        </DSSection>
      )}

      {show('Formats') && (
        <DSSection title="Formats">
          <div>
            <DSLabel hint={FORMA_TIPS.specNumberFormat}>Spec # Format</DSLabel>
            <Input
              value={doc.specNumberFormat || '## ## ##'}
              onChange={(e) => u('specNumberFormat', e.target.value)}
              className="font-mono"
            />
            <span style={{ fontSize: 11, color: 'var(--tm)' }}>
              Use # for digits. E.g. ## ## ##, ######, ##-##-##
            </span>
          </div>
          <div>
            <DSLabel hint={FORMA_TIPS.dateFormat}>Date Format</DSLabel>
            <SearchableSelectKV
              value={doc.dateFormat || 'M/d/yyyy'}
              options={[
                { value: 'M/d/yyyy', label: '1/5/2026' },
                { value: 'MM/dd/yyyy', label: '01/05/2026' },
                { value: 'yyyy/MM/dd', label: '2026/01/05' },
                { value: 'MMMM d, yyyy', label: 'January 5, 2026' },
                { value: 'MMM d, yyyy', label: 'Jan 5, 2026' },
                { value: 'd MMMM yyyy', label: '5 January 2026' },
                { value: 'MM-dd-yyyy', label: '01-05-2026' },
                { value: 'yyyy-MM-dd', label: '2026-01-05' },
              ]}
              onChange={(v) => u('dateFormat', v)}
              searchPlaceholder="Search formats…"
              clearable={false}
              style={DS_INPUT}
            />
          </div>
        </DSSection>
      )}

      {show('Layout Options') && (
        <DSSection title="Layout Options">
          {/* First-page and odd/even variants are MUTUALLY EXCLUSIVE — turning
              one on turns the other off (a forma edits one axis at a time). */}
          <DSToggle
            on={!!doc.firstPageDifferent}
            label="First page different"
            hint={FORMA_TIPS.firstPageDifferent}
            onToggle={() =>
              onUpdate({
                ...doc,
                firstPageDifferent: !doc.firstPageDifferent,
                ...(!doc.firstPageDifferent ? { oddEvenDifferent: false } : {}),
              })
            }
          />
          <DSToggle
            on={!!doc.oddEvenDifferent}
            label="Odd/even pages different"
            hint={FORMA_TIPS.oddEvenDifferent}
            onToggle={() =>
              onUpdate({
                ...doc,
                oddEvenDifferent: !doc.oddEvenDifferent,
                ...(!doc.oddEvenDifferent ? { firstPageDifferent: false } : {}),
              })
            }
          />
          <DSToggle
            on={!!doc.widowOrphan}
            label="Widow/orphan control"
            hint={FORMA_TIPS.widowOrphan}
            onToggle={() => u('widowOrphan', !doc.widowOrphan)}
          />
          {/* Which variant the canvas shows — lives here because the two
              toggles above are what create the variants. */}
          {onPageVariant && pageVariants.length > 1 && (
            <div>
              <DSLabel hint={FORMA_TIPS.pageVariantSwitcher}>Editing</DSLabel>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                {pageVariants.map((pvo) => {
                  const active = (pageVariant ?? 'odd') === pvo;
                  const label =
                    pvo === 'first'
                      ? 'First Page'
                      : pvo === 'even'
                        ? 'Even Pages'
                        : doc.oddEvenDifferent
                          ? 'Odd Pages'
                          : doc.firstPageDifferent
                            ? 'Other Pages'
                            : 'All Pages';
                  return (
                    <Button
                      key={pvo}
                      variant={active ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => onPageVariant(pvo)}
                    >
                      {label}
                    </Button>
                  );
                })}
              </div>
            </div>
          )}
        </DSSection>
      )}
    </div>
  );
}

/* ─── Edit History (compact list of recent editors) ─── */
function EditHistory({ entries, max = 3 }: { entries: any[]; max?: number }) {
  const shown = entries.slice(0, max);
  const fmtTime = (iso: string) => {
    const d = new Date(iso);
    const now = new Date();
    const diff = now.getTime() - d.getTime();
    if (diff < 60000) return 'just now';
    if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
    if (diff < 86400000) return `${Math.floor(diff / 3600000)}h ago`;
    if (diff < 604800000) return `${Math.floor(diff / 86400000)}d ago`;
    return d.toLocaleDateString();
  };
  const actionLabel = (a: string) =>
    a === 'created'
      ? 'Created'
      : a === 'published'
        ? 'Published'
        : a === 'duplicated'
          ? 'Duplicated'
          : 'Edited';
  const initials = (name: string) =>
    name
      .split(/\s+/)
      .map((w) => w[0]?.toUpperCase())
      .join('')
      .slice(0, 2);
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      {shown.map((e, i) => (
        <div
          key={i}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 6,
            fontSize: 10,
            color: 'var(--tm)',
          }}
        >
          <div
            style={{
              width: 18,
              height: 18,
              borderRadius: '50%',
              background: 'var(--ac)',
              color: '#fff',
              fontSize: 11,
              fontWeight: 700,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            {initials(e.userName)}
          </div>
          <span style={{ fontWeight: 600, color: 'var(--tx)' }}>{e.userName}</span>
          <span>{actionLabel(e.action)}</span>
          <span style={{ marginLeft: 'auto', opacity: 0.7 }}>{fmtTime(e.timestamp)}</span>
        </div>
      ))}
      {entries.length > max && (
        <div style={{ fontSize: 11, color: 'var(--tm)', opacity: 0.6, paddingLeft: 24 }}>
          +{entries.length - max} more
        </div>
      )}
    </div>
  );
}

/* ─── History Button (icon + dropdown popover) ─── */
function HistoryButton({ entries, style }: { entries: any[]; style?: React.CSSProperties }) {
  const [open, setOpen] = eUseState(false);
  const ref = React.useRef<HTMLDivElement>(null);
  eUseEffect(() => {
    if (!open) return;
    const close = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [open]);
  const historyIcon = React.createElement(
    'svg',
    {
      width: 15,
      height: 15,
      viewBox: '0 0 24 24',
      fill: 'none',
      stroke: 'currentColor',
      strokeWidth: 2,
      strokeLinecap: 'round',
      strokeLinejoin: 'round',
    },
    React.createElement('path', { d: 'M3 3v5h5' }),
    React.createElement('path', { d: 'M3.05 13A9 9 0 1 0 6 5.3L3 8' }),
    React.createElement('path', { d: 'M12 7v5l4 2' })
  );
  return (
    <div ref={ref} style={{ position: 'relative', ...style }}>
      <button
        onClick={() => setOpen((o) => !o)}
        title="Edit history"
        style={{
          padding: '5px 10px',
          borderRadius: 5,
          border: `1px solid ${open ? 'var(--ac)' : 'var(--bd)'}`,
          background: open ? 'var(--ac)' : 'var(--sf)',
          color: open ? '#fff' : 'var(--tm)',
          fontSize: 12,
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          gap: 4,
          transition: 'all .15s',
        }}
      >
        {historyIcon}
      </button>
      {open && (
        <div
          style={{
            position: 'absolute',
            top: '100%',
            right: 0,
            marginTop: 6,
            width: 280,
            background: 'var(--sf)',
            border: '1px solid var(--bd)',
            borderRadius: 8,
            boxShadow: '0 8px 24px rgba(0,0,0,.15)',
            zIndex: 100,
            padding: 12,
          }}
        >
          <div
            style={{
              fontSize: 11,
              fontWeight: 700,
              color: 'var(--tm)',
              textTransform: 'uppercase',
              letterSpacing: '.04em',
              marginBottom: 8,
            }}
          >
            Edit History
          </div>
          {!entries || entries.length === 0 ? (
            <div style={{ fontSize: 11, color: 'var(--tm)', opacity: 0.6 }}>
              No edit history yet
            </div>
          ) : (
            <EditHistory entries={entries} max={6} />
          )}
        </div>
      )}
    </div>
  );
}

/* ─── Standards Library ─── */
/** Relative "when" for the library list (compact, quiet). */
function libTimeAgo(iso?: string): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '—';
  const diff = Date.now() - d.getTime();
  if (diff < 60000) return 'just now';
  if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
  if (diff < 86400000) return `${Math.floor(diff / 3600000)}h ago`;
  if (diff < 604800000) return `${Math.floor(diff / 86400000)}d ago`;
  return d.toLocaleDateString();
}

function Library({
  standards,
  defaultId,
  onLoad,
  onOpenClassic,
  onCreate,
  onDup,
  onDel,
  onImport,
  onFieldEdit,
}: {
  standards: any[];
  defaultId: string;
  onLoad: (s: any) => void;
  /** Fall back to the classic (pre-Studio) master forma editor for this row —
   *  parity isn't proven yet, so this stays reachable (docs/DESIGNER_UNIFIED_PLAN.md §3 R6). */
  onOpenClassic?: (s: any) => void;
  onCreate: () => void;
  onImport: () => void;
  onDup: (id: string) => void;
  onDel: (id: string) => void;
  onFieldEdit?: (id: string, field: string, value: string) => void;
}) {
  // Canonical table (DataTable) — the SAME list UX as Spec Masters / Projects /
  // Team etc.: sticky header, sort, search, group-by, grid edit, actions.
  const columns: Column<any>[] = [
    {
      key: 'name',
      label: 'Name',
      width: 380,
      getValue: (s) => s.docSettings?.standardName || 'Untitled',
      editable: true,
      editType: 'text',
      editKey: 'name',
      render: (s) => (
        <span className="inline-flex max-w-full items-center gap-2">
          <span className="truncate font-semibold text-text">
            {s.docSettings?.standardName || 'Untitled'}
          </span>
          {defaultId === s.id && (
            <span className="flex-shrink-0 rounded-full bg-primary-soft px-2 py-0.5 text-[10px] font-bold uppercase tracking-[0.04em] text-primary">
              Default
            </span>
          )}
        </span>
      ),
    },
    {
      key: 'client',
      label: 'Client',
      width: 260,
      getValue: (s) => s.docSettings?.clientName || '',
      editable: true,
      editType: 'text',
      editKey: 'client',
      render: (s) => <span className="text-muted">{s.docSettings?.clientName || '—'}</span>,
    },
    {
      key: 'typeface',
      label: 'Typeface',
      width: 210,
      getValue: (s) =>
        `${s.docSettings?.bodyFont || 'Arial'} · ${s.docSettings?.bodyFontSize || 12}pt`,
      render: (s) => (
        <span className="text-muted">
          {s.docSettings?.bodyFont || 'Arial'} · {s.docSettings?.bodyFontSize || 12}pt
        </span>
      ),
    },
    {
      key: 'edited',
      label: 'Last edited',
      width: 230,
      getValue: (s) => s.editHistory?.[0]?.at || s.editHistory?.[0]?.timestamp || '',
      render: (s) => {
        const last = s.editHistory?.[0];
        if (!last) return <span className="text-muted">—</span>;
        return (
          <span className="text-muted">
            <span className="text-text">{last.userName || last.name || '—'}</span>
            <span className="mx-1.5 opacity-40">·</span>
            {libTimeAgo(last.at || last.timestamp)}
          </span>
        );
      },
    },
    {
      key: 'created',
      label: 'Created',
      width: 230,
      getValue: (s) => {
        const h = s.editHistory || [];
        const c = h.find((e: any) => e.action === 'created') || h[h.length - 1];
        return c?.at || c?.timestamp || '';
      },
      render: (s) => {
        const h = s.editHistory || [];
        const c = h.find((e: any) => e.action === 'created') || h[h.length - 1];
        if (!c) return <span className="text-muted">—</span>;
        return (
          <span className="text-muted">
            <span className="text-text">{c.userName || c.name || '—'}</span>
            <span className="mx-1.5 opacity-40">·</span>
            {libTimeAgo(c.at || c.timestamp)}
          </span>
        );
      },
    },
  ];

  const groupOptions: GroupOption<any>[] = [
    {
      key: 'client',
      label: 'By Client',
      getValue: (s) => s.docSettings?.clientName || 'No Client',
    },
    {
      key: 'typeface',
      label: 'By Typeface',
      getValue: (s) => s.docSettings?.bodyFont || 'Arial',
    },
  ];

  return (
    <div className="flex h-full w-full flex-col px-6 py-5">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="m-0 text-[22px] font-bold leading-tight text-text">Master Formas</h2>
          <p className="m-0 mt-0.5 text-[13px] text-muted">
            Word formatting templates — one per client standard. Projects get their own copy on
            apply.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={onImport}>
            <Upload size={15} strokeWidth={1.8} className="mr-1.5" />
            Import .dotx
          </Button>
          <Button onClick={onCreate}>
            <Plus size={15} strokeWidth={2.2} className="mr-1.5" />
            New Forma
          </Button>
        </div>
      </div>
      <div className="min-h-0 flex-1">
        <DataTable<any>
          columns={columns}
          data={standards}
          keyField="id"
          onRowClick={(s) => onLoad(s)}
          groupOptions={groupOptions}
          searchPlaceholder="Search formas…"
          initialSort={{ key: 'name', direction: 'asc' }}
          storageKey="formas-library-table-v2"
          emptyTitle="No saved formas"
          emptyMessage="Create a new forma or import a .dotx to get started."
          onCellEdit={
            onFieldEdit
              ? (item, key, value) => {
                  if (key === 'name') onFieldEdit(item.id, 'standardName', String(value));
                  else if (key === 'client') onFieldEdit(item.id, 'clientName', String(value));
                }
              : undefined
          }
          actions={(s) => (
            <div className="flex items-center gap-0.5">
              {onOpenClassic && (
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7 text-muted hover:text-text"
                  title="Open in classic editor"
                  aria-label="Open in classic editor"
                  onClick={(e) => {
                    e.stopPropagation();
                    onOpenClassic(s);
                  }}
                >
                  <History size={13} strokeWidth={1.8} />
                </Button>
              )}
              <Button
                variant="ghost"
                size="icon"
                className="h-7 w-7 text-muted hover:text-text"
                title="Duplicate"
                aria-label="Duplicate forma"
                onClick={(e) => {
                  e.stopPropagation();
                  onDup(s.id);
                }}
              >
                <Copy size={13} strokeWidth={1.8} />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                className="h-7 w-7 text-muted hover:text-danger"
                title="Delete"
                aria-label="Delete forma"
                onClick={(e) => {
                  e.stopPropagation();
                  if (confirm(`Delete "${s.docSettings?.standardName}"?`)) onDel(s.id);
                }}
              >
                <Trash2 size={13} strokeWidth={1.8} />
              </Button>
            </div>
          )}
        />
      </div>
    </div>
  );
}

/* ─── Batch Formatter ─── */
function Formatter({
  standards,
  lastActiveId,
  settings,
  onSettingsChange,
}: {
  standards: any[];
  lastActiveId: string | null;
  settings: any;
  onSettingsChange: (s: any) => void;
}) {
  const [sel, setSel] = eUseState(lastActiveId || settings?.defaultTemplateId || '');
  const [files, setFiles] = eUseState<string[]>([]);
  const [outputDir, setOutputDir] = eUseState(settings?.exportDir || '');
  const [running, setRunning] = eUseState(false);
  const [results, setResults] = eUseState<any[] | null>(null);
  const [facets, setFacets] = eUseState<Record<string, boolean>>({
    headers: true,
    footers: true,
    prePost: true,
    body: true,
    pageSetup: true,
  });
  const [overwriteModal, setOverwriteModal] = eUseState<any>(null);
  const [openAfterFormat, setOpenAfterFormat] = eUseState(true);
  const [exportPdf, setExportPdf] = eUseState(false);
  const [pdfResults, setPdfResults] = eUseState<any[] | null>(null);
  // Cockpit functions — Format and Convert-to-PDF are independent, either/both can run.
  const [fnFormat, setFnFormat] = eUseState(true);
  const [dragActive, setDragActive] = eUseState(false);
  // Live run progress — which phase, how far along ("Formatting 3 of 12…").
  const [progress, setProgress] = eUseState<{
    phase: 'format' | 'pdf';
    done: number;
    total: number;
  } | null>(null);
  // Inline run warning — replaces the old native alert()s.
  const [runWarning, setRunWarning] = eUseState<string | null>(null);
  // Transient notice under the drop zone ("N non-.docx files skipped").
  const [dropNotice, setDropNotice] = eUseState<string | null>(null);
  const dropNoticeTimer = eUseRef<ReturnType<typeof setTimeout> | null>(null);
  // Word COM availability — null while probing; false disables Convert to PDF.
  const [wordAvailable, setWordAvailable] = eUseState<boolean | null>(null);

  eUseEffect(() => {
    if (lastActiveId) setSel(lastActiveId);
  }, [lastActiveId]);

  // Probe for Microsoft Word once on mount (main caches the answer for the
  // session). If the probe itself is unavailable or errors, stay permissive —
  // the conversion path reports its own failure with a friendly message.
  eUseEffect(() => {
    let alive = true;
    const api = (window as any).api;
    if (api?.checkWordAvailable) {
      api
        .checkWordAvailable()
        .then((ok: boolean) => {
          if (alive) setWordAvailable(!!ok);
        })
        .catch(() => {
          if (alive) setWordAvailable(true);
        });
    } else {
      setWordAvailable(true);
    }
    return () => {
      alive = false;
    };
  }, []);

  eUseEffect(() => {
    return () => {
      if (dropNoticeTimer.current) clearTimeout(dropNoticeTimer.current);
    };
  }, []);

  // If the probe comes back "no Word" while Convert-to-PDF was already on,
  // switch it off — the disabled toggle explains why.
  eUseEffect(() => {
    if (wordAvailable === false) setExportPdf(false);
  }, [wordAvailable]);

  const togFacet = (k: string) => setFacets((p) => ({ ...p, [k]: !p[k] }));
  const anyFacet = Object.values(facets).some(Boolean);
  const pdfBlocked = wordAvailable === false;

  // Why can't Run run? Listed inline so the disabled button explains itself.
  const runBlockers: string[] = [];
  if (!files.length) runBlockers.push('Add at least one .docx file');
  if (fnFormat && !sel) runBlockers.push('Select a forma');
  if (!outputDir) runBlockers.push('Choose an output folder');
  if (!fnFormat && !exportPdf)
    runBlockers.push('Turn on at least one function — Format or Convert to PDF');
  const runDisabled = running || runBlockers.length > 0 || (fnFormat && !anyFacet);

  /** Translate raw engine errors into plain English; raw text stays in the tooltip. */
  function friendlyError(raw: string | undefined, verb: 'Formatting' | 'PDF conversion'): string {
    const r = raw || '';
    if (/end of central directory|is this a zip file|corrupted zip|invalid zip/i.test(r))
      return "This file isn't a valid Word document (.docx).";
    if (
      /ENOENT|no such file or directory|input file not found|cannot find the (file|path)/i.test(r)
    )
      return 'File not found — it may have been moved or renamed.';
    if (/EBUSY|EPERM|EACCES|permission denied|being used by another process|locked/i.test(r))
      return 'The file is locked or in use — close it in Word and try again.';
    if (/word is not installed|word\.application|not accessible/i.test(r))
      return 'Microsoft Word is not installed or not accessible.';
    const short = r.split('\n')[0].trim().slice(0, 120);
    return short ? `${verb} failed — ${short}` : `${verb} failed.`;
  }

  /** Middle-truncate a path: keep the drive/root and the leaf folder visible. */
  function middleTruncatePath(p: string, max = 52): string {
    if (!p || p.length <= max) return p;
    const sep = p.includes('\\') ? '\\' : '/';
    const parts = p.split(/[\\/]/).filter(Boolean);
    if (parts.length <= 2)
      return p.slice(0, Math.ceil(max / 2)) + '…' + p.slice(-Math.floor(max / 2));
    const leaf = parts[parts.length - 1];
    let head = parts[0];
    let i = 1;
    while (
      i < parts.length - 1 &&
      head.length + sep.length + parts[i].length + leaf.length + 3 <= max
    ) {
      head = head + sep + parts[i];
      i++;
    }
    if (i >= parts.length - 1) return p;
    return `${head}${sep}…${sep}${leaf}`;
  }

  function setOutputDirAndSave(dir: string) {
    setOutputDir(dir);
    if (onSettingsChange) onSettingsChange({ ...settings, exportDir: dir });
  }

  /** Append new paths, deduped case-insensitively (Windows paths). */
  function addFiles(paths: string[]) {
    setFiles((prev) => {
      const seen = new Set(prev.map((p) => p.toLowerCase()));
      const next = [...prev];
      for (const p of paths) {
        const key = p.toLowerCase();
        if (!seen.has(key)) {
          seen.add(key);
          next.push(p);
        }
      }
      return next;
    });
  }

  function showDropNotice(msg: string) {
    setDropNotice(msg);
    if (dropNoticeTimer.current) clearTimeout(dropNoticeTimer.current);
    dropNoticeTimer.current = setTimeout(() => setDropNotice(null), 5000);
  }

  async function pickFiles() {
    if (!(window as any).api) return;
    const paths = await (window as any).api.openDocxDialog();
    if (paths && paths.length) addFiles(paths);
  }

  async function pickOutputDir() {
    if (!(window as any).api) return;
    const dir = await (window as any).api.selectFolderDialog();
    if (dir) setOutputDirAndSave(dir);
  }

  async function handleFormat(overwriteMode?: string) {
    if (!sel) return setRunWarning('Select a forma first.');
    if (!files.length) return setRunWarning('Add at least one .docx file first.');
    if (!outputDir) return setRunWarning('Choose an output folder first.');
    if (!anyFacet) return setRunWarning('Turn on at least one part to format.');
    const config = standards.find((s) => s.id === sel);
    if (!config) return setRunWarning('Forma not found — pick another one.');

    if (!overwriteMode) {
      const conflicts: string[] = [];
      if ((window as any).api?.checkFilesExist) {
        try {
          const docxConflicts = await (window as any).api.checkFilesExist(files, outputDir);
          if (docxConflicts?.length) conflicts.push(...docxConflicts);
        } catch (e) {
          console.warn('checkFilesExist failed, proceeding:', e);
        }
      }
      // PDF outputs collide too when Convert-to-PDF is on — same modal, same choice.
      if (exportPdf && (window as any).api?.checkPdfConflicts) {
        try {
          const { conflicts: pdfConflicts } = await (window as any).api.checkPdfConflicts(
            files,
            outputDir
          );
          if (pdfConflicts?.length) conflicts.push(...pdfConflicts);
        } catch (e) {
          console.warn('checkPdfConflicts failed, proceeding:', e);
        }
      }
      if (conflicts.length > 0) {
        setOverwriteModal({
          conflicts,
          onOverwrite: () => {
            setOverwriteModal(null);
            doFormat(config, 'overwrite');
          },
          onRename: () => {
            setOverwriteModal(null);
            doFormat(config, 'rename');
          },
          onCancel: () => setOverwriteModal(null),
        });
        return;
      }
    }
    doFormat(config, overwriteMode || 'overwrite');
  }

  async function doFormat(config: any, overwriteMode: string) {
    setRunning(true);
    setRunWarning(null);
    setResults(null);
    setPdfResults(null);
    const jobFiles = [...files];
    try {
      const safeConfig = JSON.parse(JSON.stringify(config));
      // One engine call per file so the counter is real progress, not a guess.
      // Concatenated per-file results are shape-identical to one batch call.
      const allResults: any[] = [];
      for (let i = 0; i < jobFiles.length; i++) {
        setProgress({ phase: 'format', done: i, total: jobFiles.length });
        const res = await (window as any).api.formatDocuments(
          safeConfig,
          [jobFiles[i]],
          outputDir,
          facets,
          overwriteMode
        );
        if (Array.isArray(res)) allResults.push(...res);
      }
      setResults(allResults);

      // Convert successful outputs to PDF if requested — same overwrite choice
      // applies ('rename' never clobbers an existing PDF).
      if (exportPdf && (window as any).api?.convertToPdf) {
        const successFiles = allResults
          .filter((r: any) => r.success && r.outputPath)
          .map((r: any) => r.outputPath);
        if (successFiles.length > 0) {
          // ONE batch call — the converter opens a single Word instance for the
          // whole set. Per-file calls here boot Word once per document and turn
          // a ~10s batch into 30-60s (v2.14.13 regression, fixed).
          setProgress({ phase: 'pdf', done: -1, total: successFiles.length });
          const pdfRes = await (window as any).api.convertToPdf(
            successFiles,
            outputDir,
            overwriteMode
          );
          if (Array.isArray(pdfRes)) setPdfResults(pdfRes);
        }
      }

      if (openAfterFormat && allResults.some((r: any) => r.success) && outputDir) {
        (window as any).api.openFolder(outputDir);
      }
    } catch (err: any) {
      setResults([{ file: 'Error', success: false, error: err.message }]);
    }
    setProgress(null);
    setRunning(false);
  }

  // Cockpit run: branch on which functions are on. Format runs the forma pipeline
  // (and converts to PDF too if that function is on); PDF-only converts the raw .docx.
  async function handleRun() {
    setRunWarning(null);
    if (!files.length) return setRunWarning('Add at least one .docx file first.');
    if (!outputDir) return setRunWarning('Choose an output folder first.');
    if (!fnFormat && !exportPdf)
      return setRunWarning('Turn on at least one function — Format or Convert to PDF.');
    if (fnFormat) {
      await handleFormat();
    } else {
      await handlePdfOnly();
    }
  }

  async function handlePdfOnly(overwriteMode?: string) {
    if (!(window as any).api?.convertToPdf)
      return setRunWarning('PDF conversion requires Microsoft Word.');
    // PDF pre-flight: prompt before clobbering existing PDFs, same modal as docx.
    if (!overwriteMode && (window as any).api?.checkPdfConflicts) {
      try {
        const { conflicts } = await (window as any).api.checkPdfConflicts(files, outputDir);
        if (conflicts?.length) {
          setOverwriteModal({
            conflicts,
            onOverwrite: () => {
              setOverwriteModal(null);
              doPdfOnly('overwrite');
            },
            onRename: () => {
              setOverwriteModal(null);
              doPdfOnly('rename');
            },
            onCancel: () => setOverwriteModal(null),
          });
          return;
        }
      } catch (e) {
        console.warn('checkPdfConflicts failed, proceeding:', e);
      }
    }
    await doPdfOnly(overwriteMode || 'overwrite');
  }

  async function doPdfOnly(overwriteMode: string) {
    setRunning(true);
    setRunWarning(null);
    setResults(null);
    setPdfResults(null);
    const jobFiles = [...files];
    try {
      // ONE batch call — single Word instance for the whole set (per-file calls
      // boot Word per document; that was the v2.14.13 slowdown).
      setProgress({ phase: 'pdf', done: -1, total: jobFiles.length });
      const pdfRes = await (window as any).api.convertToPdf(jobFiles, outputDir, overwriteMode);
      setPdfResults(Array.isArray(pdfRes) ? pdfRes : []);
      if (openAfterFormat && outputDir && (window as any).api?.openFolder) {
        (window as any).api.openFolder(outputDir);
      }
    } catch (err: any) {
      setPdfResults([{ success: false, inputFile: 'Error', error: err.message }]);
    }
    setProgress(null);
    setRunning(false);
  }

  const CB = ({ k, label, desc }: { k: string; label: string; desc: string }) => {
    const on = facets[k];
    return (
      <label
        style={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: 8,
          cursor: 'pointer',
          padding: '6px 0',
        }}
      >
        <div
          onClick={() => togFacet(k)}
          role="checkbox"
          aria-checked={on}
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              togFacet(k);
            }
          }}
          style={{
            width: 18,
            height: 18,
            borderRadius: 4,
            border: `2px solid ${on ? 'var(--ac)' : 'var(--bd)'}`,
            background: on ? 'var(--ac)' : 'transparent',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'var(--color-text-inverse)',
            cursor: 'pointer',
            transition: 'all .15s',
            flexShrink: 0,
            marginTop: 1,
          }}
        >
          {on ? <Check size={12} aria-hidden="true" /> : null}
        </div>
        <div>
          <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--tx)' }}>{label}</div>
          <div style={{ fontSize: 11, color: 'var(--tm)', marginTop: 1 }}>{desc}</div>
        </div>
      </label>
    );
  };

  return (
    <div style={{ padding: 24, maxWidth: 1000, margin: '0 auto' }}>
      <h2 style={{ fontSize: 22, fontWeight: 700, margin: '0 0 4px' }}>Formatter</h2>
      <p style={{ fontSize: 13, color: 'var(--tm)', margin: '0 0 18px' }}>
        One engine for every output — format raw files or convert to PDF.
      </p>
      <div style={{ display: 'flex', gap: 16, alignItems: 'flex-start' }}>
        {/* LEFT — set up the job: source · forma · output */}
        <div style={{ flex: 1.4, minWidth: 0 }}>
          {/* forma + parts moved into the Format function on the right */}
          <div
            style={{
              background: 'var(--sf)',
              borderRadius: 10,
              border: '1px solid var(--bd)',
              overflow: 'hidden',
              marginBottom: 12,
            }}
          >
            <div
              style={{
                padding: '12px 16px',
                borderBottom: '1px solid var(--bd)',
                fontWeight: 600,
                fontSize: 14,
                display: 'flex',
                alignItems: 'center',
                gap: 8,
              }}
            >
              Input files
              {files.length > 0 && (
                <span
                  style={{
                    fontSize: 11,
                    fontWeight: 600,
                    color: 'var(--ac)',
                    background: 'rgba(var(--acr),0.12)',
                    borderRadius: 'var(--radius-full)',
                    padding: '2px 8px',
                  }}
                >
                  {files.length} file{files.length > 1 ? 's' : ''}
                </span>
              )}
            </div>
            <div style={{ padding: 16 }}>
              <button
                onClick={pickFiles}
                disabled={running}
                onDragOver={(e: React.DragEvent<HTMLButtonElement>) => {
                  e.preventDefault();
                  e.stopPropagation();
                  if (!dragActive) setDragActive(true);
                }}
                onDragEnter={() => setDragActive(true)}
                onDragLeave={(e: React.DragEvent<HTMLButtonElement>) => {
                  if (!e.currentTarget.contains(e.relatedTarget as Node)) setDragActive(false);
                }}
                onDrop={(e: React.DragEvent<HTMLButtonElement>) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setDragActive(false);
                  if (running) return;
                  const getPath = (window as any).api?.getPathForFile;
                  const all = [...e.dataTransfer.files];
                  const docx = all.filter((f) => f.name.toLowerCase().endsWith('.docx'));
                  const skipped = all.length - docx.length;
                  if (skipped > 0)
                    showDropNotice(
                      `Only .docx files are supported — ${skipped} file${skipped > 1 ? 's' : ''} skipped.`
                    );
                  const dropped = docx
                    .map((f) => (getPath ? getPath(f) : (f as any).path))
                    .filter((p: unknown): p is string => typeof p === 'string' && p.length > 0);
                  if (dropped.length) addFiles(dropped);
                }}
                style={{
                  width: '100%',
                  border: `2px dashed ${dragActive ? 'var(--ac)' : 'var(--bd)'}`,
                  borderRadius: 10,
                  padding: 24,
                  textAlign: 'center',
                  cursor: running ? 'default' : 'pointer',
                  opacity: running ? 0.6 : 1,
                  background: dragActive ? 'rgba(var(--acr),0.06)' : 'transparent',
                  color: 'var(--tx)',
                  transition: 'border-color .15s, background .15s',
                }}
              >
                <div style={{ marginBottom: 8, color: 'var(--tm)', opacity: 0.5 }}>
                  <svg
                    width="28"
                    height="28"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    style={{ display: 'inline-block' }}
                  >
                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                    <path d="M14 2v6h6" />
                    <path d="M12 18v-6" />
                    <path d="m9 15 3 3 3-3" />
                  </svg>
                </div>
                <div style={{ fontWeight: 600, fontSize: 13 }}>
                  {files.length
                    ? `${files.length} file${files.length > 1 ? 's' : ''} selected`
                    : 'Click to browse or drag .docx files here'}
                </div>
              </button>
              {dropNotice && (
                <div
                  role="status"
                  aria-live="polite"
                  style={{
                    marginTop: 8,
                    padding: '8px 12px',
                    borderRadius: 6,
                    background: 'color-mix(in srgb, var(--color-warning) 12%, transparent)',
                    border: '1px solid color-mix(in srgb, var(--color-warning) 30%, transparent)',
                    fontSize: 13,
                    color: 'var(--color-warning)',
                  }}
                >
                  {dropNotice}
                </div>
              )}
              {files.length > 0 && (
                <div style={{ marginTop: 10, maxHeight: 120, overflow: 'auto' }}>
                  {files.map((f, i) => (
                    <div
                      key={i}
                      style={{
                        padding: '3px 0',
                        fontSize: 13,
                        color: 'var(--tm)',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 4,
                      }}
                    >
                      <svg
                        width="13"
                        height="13"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="1.7"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        style={{ flexShrink: 0, opacity: 0.55 }}
                      >
                        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                        <path d="M14 2v6h6" />
                      </svg>
                      <span
                        style={{
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {f.split(/[/\\]/).pop()}
                      </span>
                      <button
                        type="button"
                        disabled={running}
                        onClick={() => setFiles((p) => p.filter((_, j) => j !== i))}
                        aria-label={`Remove ${f.split(/[/\\]/).pop()}`}
                        title="Remove file"
                        style={{
                          marginLeft: 'auto',
                          cursor: running ? 'default' : 'pointer',
                          opacity: 0.5,
                          flexShrink: 0,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          padding: 8,
                          border: 'none',
                          background: 'transparent',
                          color: 'var(--tm)',
                          borderRadius: 'var(--radius-sm)',
                        }}
                      >
                        <X size={12} aria-hidden="true" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
          <div
            style={{
              background: 'var(--sf)',
              borderRadius: 10,
              border: '1px solid var(--bd)',
              overflow: 'hidden',
              marginBottom: 12,
            }}
          >
            <div
              style={{
                padding: '12px 16px',
                borderBottom: '1px solid var(--bd)',
                fontWeight: 600,
                fontSize: 14,
              }}
            >
              Output
            </div>
            <div style={{ padding: 16 }}>
              <button
                onClick={pickOutputDir}
                disabled={running}
                title={outputDir || undefined}
                style={{
                  width: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  padding: '10px 12px',
                  borderRadius: 8,
                  border: '1px solid var(--bd)',
                  background: 'var(--ib)',
                  color: outputDir ? 'var(--tx)' : 'var(--tm)',
                  fontSize: 13,
                  cursor: running ? 'default' : 'pointer',
                  opacity: running ? 0.6 : 1,
                  fontWeight: 500,
                  textAlign: 'left',
                }}
              >
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.7"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  style={{ flexShrink: 0, opacity: 0.7 }}
                >
                  <path d="M20 20a2 2 0 0 0 2-2V8a2 2 0 0 0-2-2h-7.9a2 2 0 0 1-1.69-.9L9.6 3.9A2 2 0 0 0 7.93 3H4a2 2 0 0 0-2 2v13a2 2 0 0 0 2 2Z" />
                </svg>
                <span
                  style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                >
                  {outputDir ? middleTruncatePath(outputDir) : 'Choose output folder'}
                </span>
              </button>
            </div>
          </div>
        </div>
        {/* RIGHT — the run engine */}
        <div style={{ flex: 1, minWidth: 0, position: 'sticky', top: 24 }}>
          <div
            style={{
              background: 'var(--sf)',
              borderRadius: 10,
              border: '1px solid var(--bd)',
              overflow: 'hidden',
            }}
          >
            <div
              style={{
                padding: '12px 16px',
                borderBottom: '1px solid var(--bd)',
                fontWeight: 600,
                fontSize: 14,
              }}
            >
              Functions
            </div>
            <div style={{ padding: 16, display: 'flex', flexDirection: 'column', gap: 8 }}>
              {/* Function 1 — Format with a forma (expands to its forma + part chips) */}
              <div
                style={{
                  borderRadius: 8,
                  border: `1.5px solid ${fnFormat ? 'var(--ac)' : 'var(--bd)'}`,
                  background: fnFormat ? 'rgba(var(--acr),0.05)' : 'transparent',
                  overflow: 'visible',
                  transition: 'all .15s',
                }}
              >
                <div
                  onClick={() => setFnFormat((p) => !p)}
                  role="checkbox"
                  aria-checked={fnFormat}
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      setFnFormat((p) => !p);
                    }
                  }}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 10,
                    padding: '11px 12px',
                    cursor: 'pointer',
                  }}
                >
                  <div
                    style={{
                      width: 18,
                      height: 18,
                      borderRadius: 4,
                      flexShrink: 0,
                      border: `2px solid ${fnFormat ? 'var(--ac)' : 'var(--bd)'}`,
                      background: fnFormat ? 'var(--ac)' : 'transparent',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: 'var(--color-text-inverse)',
                    }}
                  >
                    {fnFormat ? <Check size={12} aria-hidden="true" /> : null}
                  </div>
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--tx)' }}>
                      Format with a forma
                    </div>
                    <div style={{ fontSize: 11, color: 'var(--tm)', marginTop: 1 }}>
                      Apply a forma + the parts you choose
                    </div>
                  </div>
                </div>
                {fnFormat && (
                  <div
                    style={{
                      padding: '11px 12px 12px',
                      borderTop: '1px solid var(--bd)',
                    }}
                  >
                    <SearchableSelectKV
                      value={sel}
                      options={standards.map((s) => {
                        const hint = [s.docSettings?.clientName, s.docSettings?.bodyFont]
                          .filter(Boolean)
                          .join(' · ');
                        return {
                          value: s.id,
                          label: s.docSettings?.standardName || 'Untitled',
                          hint: hint || undefined,
                        };
                      })}
                      onChange={(v) => setSel(v)}
                      disabled={running}
                      placeholder="— Select forma —"
                      searchPlaceholder="Search formas…"
                    />
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 10 }}>
                      {[
                        { k: 'body', label: 'Body' },
                        { k: 'headers', label: 'Headers' },
                        { k: 'footers', label: 'Footers' },
                        { k: 'prePost', label: 'Pre/Post' },
                        { k: 'pageSetup', label: 'Page setup' },
                      ].map((p) => (
                        <span
                          key={p.k}
                          onClick={() => togFacet(p.k)}
                          role="checkbox"
                          aria-checked={facets[p.k]}
                          tabIndex={0}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              togFacet(p.k);
                            }
                          }}
                          style={{
                            fontSize: 11,
                            padding: '4px 11px',
                            borderRadius: 20,
                            cursor: 'pointer',
                            userSelect: 'none',
                            border: `1px solid ${facets[p.k] ? 'var(--ac)' : 'var(--bd)'}`,
                            background: facets[p.k] ? 'rgba(var(--acr),0.12)' : 'transparent',
                            color: facets[p.k] ? 'var(--ac)' : 'var(--tm)',
                            transition: 'all .12s',
                          }}
                        >
                          {p.label}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Function 2 — Convert to PDF (disabled when Word isn't detected) */}
              <div
                onClick={() => {
                  if (!pdfBlocked) setExportPdf((p) => !p);
                }}
                role="checkbox"
                aria-checked={exportPdf}
                aria-disabled={pdfBlocked}
                tabIndex={pdfBlocked ? -1 : 0}
                onKeyDown={(e) => {
                  if (pdfBlocked) return;
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    setExportPdf((p) => !p);
                  }
                }}
                style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 10,
                  padding: '11px 12px',
                  borderRadius: 8,
                  border: `1.5px solid ${exportPdf ? 'var(--ac)' : 'var(--bd)'}`,
                  background: exportPdf ? 'rgba(var(--acr),0.05)' : 'transparent',
                  cursor: pdfBlocked ? 'default' : 'pointer',
                  opacity: pdfBlocked ? 0.6 : 1,
                  transition: 'all .15s',
                }}
              >
                <div
                  style={{
                    width: 18,
                    height: 18,
                    borderRadius: 4,
                    flexShrink: 0,
                    marginTop: 1,
                    border: `2px solid ${exportPdf ? 'var(--ac)' : 'var(--bd)'}`,
                    background: exportPdf ? 'var(--ac)' : 'transparent',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'var(--color-text-inverse)',
                  }}
                >
                  {exportPdf ? <Check size={12} aria-hidden="true" /> : null}
                </div>
                <div>
                  <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--tx)' }}>
                    Convert to PDF
                    <span
                      style={{ fontSize: 10, color: 'var(--tm)', marginLeft: 6, fontWeight: 400 }}
                    >
                      (needs Word)
                    </span>
                  </div>
                  {pdfBlocked ? (
                    <div style={{ fontSize: 11, color: 'var(--color-warning)', marginTop: 1 }}>
                      Requires Microsoft Word on this machine — not detected
                    </div>
                  ) : (
                    <div style={{ fontSize: 11, color: 'var(--tm)', marginTop: 1 }}>
                      {fnFormat ? 'Convert the formatted output' : 'Convert the input .docx as-is'}
                    </div>
                  )}
                </div>
              </div>

              {fnFormat && !anyFacet && (
                <div
                  style={{
                    padding: '8px 12px',
                    borderRadius: 6,
                    background: 'color-mix(in srgb, var(--color-warning) 12%, transparent)',
                    border: '1px solid color-mix(in srgb, var(--color-warning) 30%, transparent)',
                    fontSize: 13,
                    color: 'var(--color-warning)',
                  }}
                >
                  Turn on at least one part to format (Body, Headers, …) above.
                </div>
              )}

              {/* Why Run is disabled — listed inline, same treatment as the parts warning */}
              {!running && runBlockers.length > 0 && (
                <div
                  style={{
                    padding: '8px 12px',
                    borderRadius: 6,
                    background: 'color-mix(in srgb, var(--color-warning) 12%, transparent)',
                    border: '1px solid color-mix(in srgb, var(--color-warning) 30%, transparent)',
                    fontSize: 13,
                    color: 'var(--color-warning)',
                  }}
                >
                  {runBlockers.map((b, i) => (
                    <div key={i} style={{ padding: '1px 0' }}>
                      {b}
                    </div>
                  ))}
                </div>
              )}

              {/* Inline run warning — replaces the old native alert()s */}
              {runWarning && (
                <div
                  role="alert"
                  style={{
                    padding: '8px 12px',
                    borderRadius: 6,
                    background: 'color-mix(in srgb, var(--color-warning) 12%, transparent)',
                    border: '1px solid color-mix(in srgb, var(--color-warning) 30%, transparent)',
                    fontSize: 13,
                    color: 'var(--color-warning)',
                  }}
                >
                  {runWarning}
                </div>
              )}

              <Button
                onClick={handleRun}
                disabled={runDisabled}
                className="w-full"
                size="lg"
                style={{
                  padding: '14px 12px',
                  height: 'auto',
                  borderRadius: 9,
                  marginTop: 6,
                  fontWeight: 700,
                  fontSize: 16,
                  boxShadow: runDisabled ? 'none' : '0 6px 16px rgba(var(--acr),0.3)',
                }}
              >
                {running ? (
                  <>
                    <Loader2 size={16} aria-hidden="true" className="animate-spin" />
                    <span aria-live="polite">
                      {progress
                        ? progress.done < 0
                          ? `Converting ${progress.total} file${progress.total === 1 ? '' : 's'} to PDF…`
                          : `${progress.phase === 'format' ? 'Formatting' : 'Converting to PDF'} ${Math.min(
                              progress.done + 1,
                              progress.total
                            )} of ${progress.total}…`
                        : 'Running…'}
                    </span>
                  </>
                ) : (
                  <>
                    <Play size={16} aria-hidden="true" />
                    {`Run${files.length ? ` · ${files.length} file${files.length > 1 ? 's' : ''}` : ''}`}
                  </>
                )}
              </Button>
              <label
                onClick={() => setOpenAfterFormat((p) => !p)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6,
                  cursor: 'pointer',
                  marginTop: 2,
                  fontSize: 13,
                  color: 'var(--tm)',
                }}
              >
                <div
                  role="checkbox"
                  aria-checked={openAfterFormat}
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      setOpenAfterFormat((p) => !p);
                    }
                  }}
                  style={{
                    width: 14,
                    height: 14,
                    borderRadius: 3,
                    border: `1.5px solid ${openAfterFormat ? 'var(--ac)' : 'var(--bd)'}`,
                    background: openAfterFormat ? 'var(--ac)' : 'transparent',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'var(--color-text-inverse)',
                    flexShrink: 0,
                    transition: 'all .15s',
                  }}
                >
                  {openAfterFormat ? <Check size={9} aria-hidden="true" /> : null}
                </div>
                Open output folder when done
              </label>
              {results && (
                <div style={{ marginTop: 12 }}>
                  <div
                    style={{
                      fontSize: 11,
                      fontWeight: 700,
                      color: 'var(--tm)',
                      textTransform: 'uppercase',
                      letterSpacing: '.04em',
                      marginBottom: 4,
                    }}
                  >
                    Formatting results
                  </div>
                  {results.map((r, i) => (
                    <div
                      key={i}
                      style={{
                        padding: '6px 0',
                        fontSize: 13,
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: 6,
                        borderBottom: '1px solid var(--bd)',
                      }}
                    >
                      {r.success ? (
                        <CheckCircle2
                          size={16}
                          aria-hidden="true"
                          style={{ color: 'var(--ok)', flexShrink: 0, marginTop: 2 }}
                        />
                      ) : (
                        <XCircle
                          size={16}
                          aria-hidden="true"
                          style={{ color: 'var(--dg)', flexShrink: 0, marginTop: 2 }}
                        />
                      )}
                      <span className="sr-only">{r.success ? 'Succeeded: ' : 'Failed: '}</span>
                      <span
                        style={{
                          fontWeight: 600,
                          flex: 1,
                          minWidth: 0,
                          overflowWrap: 'anywhere',
                        }}
                      >
                        {r.file}
                      </span>
                      {r.success ? (
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-6 w-6 text-muted"
                          title="Show in folder"
                          aria-label={`Show ${r.file} in folder`}
                          onClick={() => {
                            const p = (r as any).outputPath;
                            const dir = p ? p.replace(/[\\/][^\\/]*$/, '') : outputDir;
                            if (dir) (window as any).api?.openFolder?.(dir);
                          }}
                        >
                          <FolderOpen size={13} strokeWidth={1.8} />
                        </Button>
                      ) : (
                        <span
                          title={r.error}
                          style={{
                            color: 'var(--dg)',
                            fontSize: 11,
                            maxWidth: '55%',
                            overflowWrap: 'anywhere',
                            textAlign: 'right',
                            marginTop: 2,
                          }}
                        >
                          {friendlyError(r.error, 'Formatting')}
                        </span>
                      )}
                    </div>
                  ))}
                </div>
              )}
              {pdfResults && (
                <div style={{ marginTop: 8 }}>
                  <div
                    style={{
                      fontSize: 11,
                      fontWeight: 700,
                      color: 'var(--tm)',
                      textTransform: 'uppercase',
                      letterSpacing: '.04em',
                      marginBottom: 4,
                    }}
                  >
                    PDF export results
                  </div>
                  {pdfResults.map((r: any, i: number) => (
                    <div
                      key={i}
                      style={{
                        padding: '6px 0',
                        fontSize: 13,
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: 6,
                        borderBottom: '1px solid var(--bd)',
                      }}
                    >
                      {r.success ? (
                        <CheckCircle2
                          size={16}
                          aria-hidden="true"
                          style={{ color: 'var(--ok)', flexShrink: 0, marginTop: 2 }}
                        />
                      ) : (
                        <XCircle
                          size={16}
                          aria-hidden="true"
                          style={{ color: 'var(--dg)', flexShrink: 0, marginTop: 2 }}
                        />
                      )}
                      <span className="sr-only">{r.success ? 'Succeeded: ' : 'Failed: '}</span>
                      <span
                        style={{
                          fontWeight: 600,
                          flex: 1,
                          minWidth: 0,
                          overflowWrap: 'anywhere',
                        }}
                      >
                        {(r.outputFile || r.inputFile || '').split(/[/\\]/).pop()}
                      </span>
                      {r.success ? (
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-6 w-6 text-muted"
                          title="Show in folder"
                          aria-label={`Show ${(r.outputFile || r.inputFile || '').split(/[/\\]/).pop()} in folder`}
                          onClick={() => {
                            const p = r.outputFile;
                            const dir = p ? p.replace(/[\\/][^\\/]*$/, '') : outputDir;
                            if (dir) (window as any).api?.openFolder?.(dir);
                          }}
                        >
                          <FolderOpen size={13} strokeWidth={1.8} />
                        </Button>
                      ) : (
                        <span
                          title={r.error}
                          style={{
                            color: 'var(--dg)',
                            fontSize: 11,
                            maxWidth: '55%',
                            overflowWrap: 'anywhere',
                            textAlign: 'right',
                            marginTop: 2,
                          }}
                        >
                          {friendlyError(r.error, 'PDF conversion')}
                        </span>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Overwrite Confirmation Modal */}
      {overwriteModal && (
        <div
          onClick={overwriteModal.onCancel}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            background: 'rgba(0,0,0,.45)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
          }}
        >
          <div
            onClick={(e: React.MouseEvent<HTMLDivElement>) => e.stopPropagation()}
            style={{
              background: 'var(--sf)',
              borderRadius: 10,
              padding: 20,
              width: 420,
              border: '1px solid var(--bd)',
              boxShadow: 'var(--shadow-xl)',
            }}
          >
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                fontWeight: 700,
                fontSize: 16,
                marginBottom: 8,
              }}
            >
              <AlertTriangle
                size={16}
                aria-hidden="true"
                style={{ color: 'var(--color-warning)' }}
              />
              Files already exist
            </div>
            <div style={{ fontSize: 13, color: 'var(--tm)', marginBottom: 12 }}>
              {overwriteModal.conflicts.length} file{overwriteModal.conflicts.length > 1 ? 's' : ''}{' '}
              already exist{overwriteModal.conflicts.length === 1 ? 's' : ''} in the output folder:
            </div>
            <div
              style={{
                maxHeight: 120,
                overflow: 'auto',
                marginBottom: 16,
                padding: 8,
                background: 'var(--ib)',
                borderRadius: 6,
                fontSize: 13,
              }}
            >
              {overwriteModal.conflicts.map((f: string, i: number) => (
                <div
                  key={i}
                  style={{
                    padding: '2px 0',
                    color: 'var(--tm)',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 6,
                  }}
                >
                  <FileText size={13} aria-hidden="true" style={{ flexShrink: 0, opacity: 0.7 }} />
                  {f.split(/[/\\]/).pop()}
                </div>
              ))}
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <Button
                variant="outline"
                onClick={overwriteModal.onCancel}
                className="flex-1"
                size="sm"
              >
                Cancel
              </Button>
              <Button onClick={overwriteModal.onRename} className="flex-1" size="sm">
                Rename (add _new)
              </Button>
              <Button
                variant="destructive"
                onClick={overwriteModal.onOverwrite}
                className="flex-1"
                size="sm"
              >
                Overwrite
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* ─── Shared modal styles (PDF converter prompts) ─── */
const overlayStyle: React.CSSProperties = {
  position: 'fixed',
  inset: 0,
  background: 'rgba(0,0,0,.45)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 1000,
};
const modalStyle: React.CSSProperties = {
  background: 'var(--sf)',
  borderRadius: 10,
  padding: 24,
  border: '1px solid var(--bd)',
  boxShadow: '0 8px 32px rgba(0,0,0,.2)',
  maxHeight: '80vh',
  overflow: 'auto',
};
const secondaryBtn: React.CSSProperties = {
  padding: '8px 16px',
  borderRadius: 6,
  border: '1px solid var(--bd)',
  background: 'transparent',
  color: 'var(--tm)',
  fontSize: 12,
  cursor: 'pointer',
};

/* ─── Standalone PDF Converter ─── */
function PdfConverter() {
  const [pdfFiles, setPdfFiles] = eUseState<string[]>([]);
  const [pdfOutputDir, setPdfOutputDir] = eUseState('');
  const [pdfRunning, setPdfRunning] = eUseState(false);
  const [pdfRes, setPdfRes] = eUseState<any[] | null>(null);
  const [pdfOpenAfter, setPdfOpenAfter] = eUseState(true);
  // Protective prompts before any overwrite or silent folder creation.
  const [pdfModal, setPdfModal] = eUseState<{
    kind: 'folder' | 'conflict';
    conflicts?: string[];
  } | null>(null);

  async function pickPdfFiles() {
    if (!(window as any).api) return;
    const paths = await (window as any).api.openDocxDialog();
    if (paths && paths.length) setPdfFiles(paths);
  }
  async function pickPdfOutputDir() {
    if (!(window as any).api) return;
    const dir = await (window as any).api.selectFolderDialog();
    if (dir) setPdfOutputDir(dir);
  }
  // Pre-flight: never overwrite or silently create a folder without asking.
  async function handleConvert() {
    if (!pdfFiles.length) return;
    if ((window as any).api?.checkPdfConflicts) {
      try {
        const { folderMissing, conflicts } = await (window as any).api.checkPdfConflicts(
          pdfFiles,
          pdfOutputDir || undefined
        );
        if (folderMissing) {
          setPdfModal({ kind: 'folder' });
          return;
        }
        if (conflicts && conflicts.length > 0) {
          setPdfModal({ kind: 'conflict', conflicts });
          return;
        }
      } catch (e) {
        console.warn('checkPdfConflicts failed, proceeding:', e);
      }
    }
    doConvert('overwrite');
  }

  async function doConvert(overwriteMode: 'overwrite' | 'rename') {
    setPdfModal(null);
    setPdfRunning(true);
    setPdfRes(null);
    try {
      const res = await (window as any).api.convertToPdf(
        pdfFiles,
        pdfOutputDir || undefined,
        overwriteMode
      );
      setPdfRes(res);
      if (pdfOpenAfter && res?.some((r: any) => r.success)) {
        const outDir =
          pdfOutputDir || (pdfFiles[0] ? pdfFiles[0].replace(/[/\\][^/\\]+$/, '') : '');
        if (outDir) (window as any).api.openFolder(outDir);
      }
    } catch (err: any) {
      setPdfRes([{ success: false, inputFile: 'Error', error: err.message }]);
    }
    setPdfRunning(false);
  }

  return (
    <>
      {pdfModal && (
        <div
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) setPdfModal(null);
          }}
          style={overlayStyle}
        >
          <div style={{ ...modalStyle, width: 460 }} onMouseDown={(e) => e.stopPropagation()}>
            {pdfModal.kind === 'folder' ? (
              <>
                <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 8 }}>
                  Output folder not found
                </div>
                <div style={{ fontSize: 12, color: 'var(--tm)', marginBottom: 6, lineHeight: 1.5 }}>
                  This folder doesn’t exist:
                </div>
                <div
                  style={{
                    fontSize: 11,
                    fontFamily: "'JetBrains Mono', monospace",
                    background: 'var(--bg)',
                    border: '1px solid var(--bd)',
                    borderRadius: 6,
                    padding: '7px 10px',
                    marginBottom: 16,
                    wordBreak: 'break-all',
                  }}
                >
                  {pdfOutputDir}
                </div>
                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                  <button onClick={() => setPdfModal(null)} style={secondaryBtn}>
                    Cancel
                  </button>
                  <button
                    onClick={() => doConvert('overwrite')}
                    style={{
                      padding: '8px 18px',
                      borderRadius: 6,
                      border: 'none',
                      background: 'var(--ac)',
                      color: '#fff',
                      fontSize: 12,
                      fontWeight: 600,
                      cursor: 'pointer',
                    }}
                  >
                    Create folder &amp; convert
                  </button>
                </div>
              </>
            ) : (
              <>
                <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 8 }}>
                  {pdfModal.conflicts!.length} PDF
                  {pdfModal.conflicts!.length === 1 ? '' : 's'} already exist
                  {pdfModal.conflicts!.length === 1 ? 's' : ''}
                </div>
                <div style={{ fontSize: 12, color: 'var(--tm)', marginBottom: 8, lineHeight: 1.5 }}>
                  These output files are already in the folder:
                </div>
                <div
                  style={{
                    maxHeight: 150,
                    overflow: 'auto',
                    background: 'var(--bg)',
                    border: '1px solid var(--bd)',
                    borderRadius: 6,
                    padding: '6px 8px',
                    marginBottom: 16,
                  }}
                >
                  {pdfModal.conflicts!.map((f, i) => (
                    <div
                      key={i}
                      style={{
                        fontSize: 10,
                        fontFamily: "'JetBrains Mono', monospace",
                        color: 'var(--tm)',
                        padding: '2px 0',
                      }}
                    >
                      {f.replace(/^.*[/\\]/, '')}
                    </div>
                  ))}
                </div>
                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                  <button onClick={() => setPdfModal(null)} style={secondaryBtn}>
                    Cancel
                  </button>
                  <button onClick={() => doConvert('rename')} style={secondaryBtn}>
                    Keep both (rename)
                  </button>
                  <button
                    onClick={() => doConvert('overwrite')}
                    style={{
                      padding: '8px 18px',
                      borderRadius: 6,
                      border: 'none',
                      background: 'var(--dg, #c0392b)',
                      color: '#fff',
                      fontSize: 12,
                      fontWeight: 600,
                      cursor: 'pointer',
                    }}
                  >
                    Overwrite
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
      <div
        style={{
          padding: '12px 16px',
          borderBottom: '1px solid var(--bd)',
          fontWeight: 600,
          fontSize: 14,
          display: 'flex',
          alignItems: 'center',
          gap: 8,
        }}
      >
        Convert to PDF
        <span style={{ fontSize: 11, fontWeight: 400, color: 'var(--tm)' }}>
          Convert .docx files to PDF without formatting
        </span>
      </div>
      <div style={{ padding: 16, display: 'flex', flexDirection: 'column', gap: 12 }}>
        <button
          onClick={pickPdfFiles}
          onDragOver={(e: React.DragEvent<HTMLButtonElement>) => {
            e.preventDefault();
            e.stopPropagation();
          }}
          onDrop={(e: React.DragEvent<HTMLButtonElement>) => {
            e.preventDefault();
            e.stopPropagation();
            const getPath = (window as any).api?.getPathForFile;
            const dropped = [...e.dataTransfer.files]
              .filter((f) => f.name.toLowerCase().endsWith('.docx'))
              .map((f) => (getPath ? getPath(f) : (f as any).path))
              .filter((p: unknown): p is string => typeof p === 'string' && p.length > 0);
            if (dropped.length) setPdfFiles((p) => [...p, ...dropped]);
          }}
          style={{
            width: '100%',
            border: '2px dashed var(--bd)',
            borderRadius: 10,
            padding: 16,
            textAlign: 'center',
            cursor: 'pointer',
            background: 'transparent',
            color: 'var(--tx)',
          }}
        >
          <div style={{ fontWeight: 600, fontSize: 13 }}>
            {pdfFiles.length
              ? `${pdfFiles.length} file${pdfFiles.length > 1 ? 's' : ''} selected`
              : 'Click to browse or drag .docx files here'}
          </div>
        </button>
        {pdfFiles.length > 0 && (
          <div style={{ maxHeight: 100, overflow: 'auto' }}>
            {pdfFiles.map((f, i) => (
              <div
                key={i}
                style={{
                  padding: '3px 0',
                  fontSize: 12,
                  color: 'var(--tm)',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 4,
                }}
              >
                <span>📄</span>
                <span
                  style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                >
                  {f.split(/[/\\]/).pop()}
                </span>
                <span
                  onClick={() => setPdfFiles((p) => p.filter((_, j) => j !== i))}
                  style={{
                    marginLeft: 'auto',
                    cursor: 'pointer',
                    opacity: 0.4,
                    fontSize: 11,
                    flexShrink: 0,
                  }}
                >
                  x
                </span>
              </div>
            ))}
          </div>
        )}
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <button
            onClick={pickPdfOutputDir}
            style={{
              padding: '6px 12px',
              borderRadius: 6,
              border: '1px solid var(--bd)',
              background: 'var(--ib)',
              color: 'var(--tx)',
              fontSize: 12,
              cursor: 'pointer',
              fontWeight: 500,
            }}
          >
            {pdfOutputDir ? pdfOutputDir : '📁 Output folder (same as source if empty)'}
          </button>
          {pdfOutputDir && (
            <span
              onClick={() => setPdfOutputDir('')}
              style={{ cursor: 'pointer', fontSize: 11, color: 'var(--tm)' }}
            >
              x clear
            </span>
          )}
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <button
            onClick={handleConvert}
            disabled={pdfRunning || !pdfFiles.length}
            style={{
              padding: '8px 20px',
              borderRadius: 6,
              border: 'none',
              background: pdfRunning || !pdfFiles.length ? 'var(--bd)' : 'var(--ac)',
              color: '#fff',
              fontWeight: 600,
              fontSize: 13,
              cursor: pdfRunning || !pdfFiles.length ? 'not-allowed' : 'pointer',
            }}
          >
            {pdfRunning ? 'Converting...' : 'Convert to PDF'}
          </button>
          <label
            onClick={() => setPdfOpenAfter((p) => !p)}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 5,
              cursor: 'pointer',
              fontSize: 12,
              color: 'var(--tm)',
            }}
          >
            <div
              style={{
                width: 14,
                height: 14,
                borderRadius: 3,
                border: `1.5px solid ${pdfOpenAfter ? 'var(--ac)' : 'var(--bd)'}`,
                background: pdfOpenAfter ? 'var(--ac)' : 'transparent',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#fff',
                fontSize: 11,
                flexShrink: 0,
                transition: 'all .15s',
              }}
            >
              {pdfOpenAfter ? '✓' : ''}
            </div>
            Open folder after
          </label>
        </div>
        <div style={{ fontSize: 10, color: 'var(--tm)' }}>
          Requires Microsoft Word installed. Uses Word's native PDF export for pixel-perfect output.
        </div>
        {pdfRes && (
          <div>
            {pdfRes.map((r: any, i: number) => (
              <div
                key={i}
                style={{
                  padding: '5px 0',
                  fontSize: 12,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6,
                  borderBottom: '1px solid var(--bd)',
                }}
              >
                <span style={{ fontSize: 14 }}>{r.success ? '✅' : '❌'}</span>
                <span style={{ fontWeight: 600 }}>
                  {(r.outputFile || r.inputFile || '').split(/[/\\]/).pop()}
                </span>
                {!r.success && (
                  <span style={{ color: 'var(--dg)', fontSize: 11, marginLeft: 'auto' }}>
                    {r.error}
                  </span>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </>
  );
}

/* ─── Settings Page ─── */
/* Password, MFA, and connected sign-ins live in the Clerk account portal —
   auth is an on-web process (docs/AUTH_CLERK_PLAN.md); the app never edits
   credentials itself. */
function AccountSecurityCard({ S }: { S: Record<string, React.CSSProperties> }) {
  return (
    <div style={S.card}>
      <div style={S.hdr}>Account & Security</div>
      <div style={{ padding: 16, display: 'flex', flexDirection: 'column', gap: 10 }}>
        <div style={{ fontSize: 12, color: 'var(--tm)', lineHeight: 1.5 }}>
          Password, sign-in methods, and security settings are managed in your SpecForma account
          portal — it opens in your browser.
        </div>
        <button
          onClick={() => {
            import('../lib/clerkConfig').then(({ CLERK_ACCOUNT_URL }) => {
              window.api?.openExternal?.(CLERK_ACCOUNT_URL);
            });
          }}
          style={{ ...S.btn, alignSelf: 'flex-start' }}
        >
          Manage Account
        </button>
      </div>
    </div>
  );
}

function Settings({
  themeMode,
  onThemeMode,
  standards,
  settings,
  onSettingsChange,
  currentUser,
}: {
  themeMode: string;
  onThemeMode: (m: string) => void;
  standards: any[];
  settings: any;
  onSettingsChange: (s: any) => void;
  currentUser: any;
}) {
  const [newAdmin, setNewAdmin] = eUseState('');
  const [appVersion, setAppVersion] = eUseState('');
  const [aiKeyNew, setAiKeyNew] = eUseState('');
  const [aiHasKey, setAiHasKey] = eUseState(false);
  const [aiMasked, setAiMasked] = eUseState('');
  const [aiKeyLoaded, setAiKeyLoaded] = eUseState(false);
  const [aiKeySaving, setAiKeySaving] = eUseState(false);
  React.useEffect(() => {
    window.api
      .getAppVersion()
      .then((v) => setAppVersion(v))
      .catch(() => {});
  }, []);
  React.useEffect(() => {
    window.api
      .loadApiKey()
      .then((r: any) => {
        setAiHasKey(r?.hasKey || false);
        setAiMasked(r?.masked || '');
        setAiKeyLoaded(true);
      })
      .catch(() => setAiKeyLoaded(true));
  }, []);
  const isAdmin = currentUser && currentUser.role === 'admin';
  const admins: string[] = settings.adminUsernames || [];

  function addAdmin() {
    const u = newAdmin.trim().toLowerCase();
    if (!u) return;
    if (admins.map((a) => a.toLowerCase()).includes(u)) return;
    onSettingsChange({ ...settings, adminUsernames: [...admins, u] });
    setNewAdmin('');
  }
  function removeAdmin(username: string) {
    if (
      username.toLowerCase() === (currentUser.email || currentUser.username || '').toLowerCase()
    ) {
      if (!confirm('Remove yourself from admins? You will lose admin access.')) return;
    }
    onSettingsChange({
      ...settings,
      adminUsernames: admins.filter((a) => a.toLowerCase() !== username.toLowerCase()),
    });
  }
  const S: Record<string, React.CSSProperties> = {
    card: {
      background: 'var(--sf)',
      borderRadius: 10,
      border: '1px solid var(--bd)',
      overflow: 'hidden',
      marginBottom: 12,
    },
    hdr: {
      padding: '12px 16px',
      borderBottom: '1px solid var(--bd)',
      fontWeight: 600,
      fontSize: 14,
    },
    inp: {
      width: '100%',
      padding: '6px 10px',
      borderRadius: 5,
      border: '1px solid var(--bd)',
      background: 'var(--ib)',
      color: 'var(--tx)',
      fontSize: 13,
      boxSizing: 'border-box',
    },
    btn: {
      padding: '6px 16px',
      borderRadius: 5,
      border: 'none',
      background: 'var(--ac)',
      color: 'var(--color-text-inverse)',
      fontWeight: 600,
      fontSize: 13,
      cursor: 'pointer',
    },
    btnSm: {
      padding: '4px 10px',
      borderRadius: 4,
      border: '1px solid var(--bd)',
      background: 'var(--ib)',
      color: 'var(--tx)',
      fontSize: 11,
      cursor: 'pointer',
      fontWeight: 500,
    },
  };

  return (
    <div style={{ padding: 24, maxWidth: 760, margin: '0 auto' }}>
      <h2 style={{ fontSize: 22, fontWeight: 700, margin: '0 0 6px' }}>Settings</h2>
      <p style={{ fontSize: 13, color: 'var(--tm)', margin: '0 0 20px' }}>
        Signed in as <b>{currentUser.displayName || currentUser.email || currentUser.username}</b> (
        {currentUser.role})
      </p>

      {/* Appearance */}
      <div style={S.card}>
        <div style={S.hdr}>Appearance</div>
        <div
          style={{
            padding: 16,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <span style={{ fontWeight: 600 }}>Theme</span>
          <div
            style={{
              display: 'flex',
              gap: 2,
              background: 'var(--sa)',
              borderRadius: 6,
              padding: 2,
            }}
          >
            {['light', 'system', 'dark'].map((m) => (
              <Button
                key={m}
                variant={themeMode === m ? 'secondary' : 'ghost'}
                size="sm"
                onClick={() => onThemeMode(m)}
                className="capitalize"
                style={{
                  fontSize: 13,
                  boxShadow: themeMode === m ? 'var(--shadow-sm)' : 'none',
                }}
              >
                {m}
              </Button>
            ))}
          </div>
        </div>
      </div>

      {/* Default Template — overflow visible so the forma dropdown isn't clipped */}
      <div style={{ ...S.card, overflow: 'visible' }}>
        <div style={S.hdr}>Default Template</div>
        <div style={{ padding: 16 }}>
          <div style={{ fontSize: 14, color: 'var(--tm)', marginBottom: 8 }}>
            Select what template to use as the "New Template" starting point.
          </div>
          <SearchableSelectKV
            value={settings.defaultTemplateId || ''}
            options={standards.map((s) => ({
              value: s.id,
              label: s.docSettings?.standardName || 'Untitled',
            }))}
            onChange={(v) => onSettingsChange({ ...settings, defaultTemplateId: v })}
            placeholder="— None —"
            searchPlaceholder="Search templates…"
            style={S.inp}
          />
        </div>
      </div>

      {/* AI Integration — desktop only: the key is stored and used by the
          local engine; the web target never reads it. */}
      {!isWeb && (
        <div style={S.card}>
          <div style={S.hdr}>AI Integration</div>
          <div style={{ padding: 16 }}>
            <div style={{ fontSize: 13, color: 'var(--tm)', marginBottom: 12, lineHeight: 1.5 }}>
              Connect an Anthropic API key to enable AI-powered features like spec classification
              and content suggestions.
            </div>
            {!aiKeyLoaded && (
              <div
                aria-hidden="true"
                style={{
                  height: 44,
                  borderRadius: 6,
                  marginBottom: 12,
                  background: 'var(--sa)',
                  animation: 'pulse 1.4s ease-in-out infinite',
                }}
              />
            )}
            {aiKeyLoaded && aiHasKey && (
              <div
                style={{
                  padding: '8px 12px',
                  borderRadius: 6,
                  background: 'rgba(var(--okr),0.06)',
                  border: '1px solid rgba(var(--okr),0.15)',
                  marginBottom: 12,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                }}
              >
                <div>
                  <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--ok)' }}>
                    API key connected
                  </div>
                  <div
                    style={{
                      fontSize: 11,
                      color: 'var(--tm)',
                      fontFamily: 'var(--font-mono)',
                      marginTop: 2,
                    }}
                  >
                    {aiMasked}
                  </div>
                </div>
                <button
                  onClick={async () => {
                    if (!confirm('Remove API key? AI features will be disabled.')) return;
                    setAiKeySaving(true);
                    try {
                      await window.api.saveApiKey('');
                      setAiHasKey(false);
                      setAiMasked('');
                    } catch {}
                    setAiKeySaving(false);
                  }}
                  style={{ ...S.btnSm, color: 'var(--dg)', borderColor: 'var(--dg)' }}
                >
                  Remove
                </button>
              </div>
            )}
            {aiKeyLoaded && !aiHasKey && (
              <div
                style={{
                  padding: '8px 12px',
                  borderRadius: 6,
                  background: 'color-mix(in srgb, var(--color-danger) 6%, transparent)',
                  border: '1px solid color-mix(in srgb, var(--color-danger) 15%, transparent)',
                  marginBottom: 12,
                }}
              >
                <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--dg)' }}>No API key</div>
                <div style={{ fontSize: 11, color: 'var(--tm)', marginTop: 2 }}>
                  AI features are disabled.
                </div>
              </div>
            )}
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <Input
                value={aiKeyNew}
                onChange={(e) => setAiKeyNew(e.target.value)}
                type="password"
                placeholder={aiHasKey ? 'Enter new key to replace...' : 'sk-ant-api03-...'}
                style={{ ...S.inp, flex: 1, fontFamily: 'var(--font-mono)', fontSize: 11 }}
              />
              <Button
                onClick={async () => {
                  if (!aiKeyNew.trim()) return;
                  setAiKeySaving(true);
                  try {
                    await window.api.saveApiKey(aiKeyNew.trim());
                    const r: any = await window.api.loadApiKey();
                    setAiHasKey(r?.hasKey || false);
                    setAiMasked(r?.masked || '');
                    setAiKeyNew('');
                  } catch {}
                  setAiKeySaving(false);
                }}
                disabled={aiKeySaving || !aiKeyNew.trim()}
                size="sm"
              >
                {aiKeySaving ? 'Saving...' : aiHasKey ? 'Replace' : 'Save'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Change Password */}
      <AccountSecurityCard S={S} />

      {/* Admin Users (hidden) */}
      {false && isAdmin && (
        <div style={S.card}>
          <div style={S.hdr}>
            Admin Users{' '}
            <span style={{ fontSize: 11, color: 'var(--tm)', fontWeight: 400 }}>(Admin)</span>
          </div>
          <div style={{ padding: 16 }}>
            <div style={{ fontSize: 11, color: 'var(--tm)', marginBottom: 10 }}>
              Windows usernames with admin access. If empty, everyone is admin.
            </div>
            {admins.length > 0 ? (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 12 }}>
                {admins.map((u) => (
                  <div
                    key={u}
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 4,
                      padding: '4px 10px',
                      borderRadius: 5,
                      background: 'rgba(var(--acr),0.08)',
                      border: '1px solid rgba(var(--acr),0.2)',
                      fontSize: 13,
                      fontWeight: 600,
                      fontFamily: 'var(--font-mono)',
                    }}
                  >
                    {u}
                    <button
                      type="button"
                      onClick={() => removeAdmin(u)}
                      aria-label={`Remove ${u}`}
                      style={{
                        cursor: 'pointer',
                        opacity: 0.5,
                        marginLeft: 2,
                        border: 'none',
                        background: 'transparent',
                        color: 'inherit',
                        display: 'flex',
                        alignItems: 'center',
                        padding: 4,
                      }}
                    >
                      <X size={11} aria-hidden="true" />
                    </button>
                  </div>
                ))}
              </div>
            ) : (
              <div
                style={{
                  padding: '8px 10px',
                  borderRadius: 6,
                  background: 'color-mix(in srgb, var(--color-warning) 12%, transparent)',
                  border: '1px solid color-mix(in srgb, var(--color-warning) 30%, transparent)',
                  fontSize: 11,
                  color: 'var(--color-warning)',
                  marginBottom: 12,
                }}
              >
                No admins configured — everyone has admin access.
              </div>
            )}
            <div style={{ display: 'flex', gap: 6 }}>
              <Input
                value={newAdmin}
                onChange={(e) => setNewAdmin(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') addAdmin();
                }}
                placeholder="Windows username (e.g. jsmith)"
                style={{ ...S.inp, flex: 1, fontFamily: 'var(--font-mono)' }}
              />
              <Button onClick={addAdmin} disabled={!newAdmin.trim()} size="sm">
                Add
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* About */}
      <div style={S.card}>
        <div style={S.hdr}>About</div>
        <div style={{ padding: 16, fontSize: 13, color: 'var(--tm)' }}>
          SpecForma™ v{appVersion || '...'}
          <br />
          Standardize, organize, and automate specification workflows.
          <br />
          <span style={{ fontSize: 11, marginTop: 4, display: 'block', opacity: 0.7 }}>
            Developed by TheBeardedLadyCo™
            <br />
            Copyright © 2026. All rights reserved.
            <br />
          </span>
          {isWeb ? (
            <span style={{ fontSize: 11, marginTop: 10, display: 'block' }}>
              Updates are automatic on the web.
            </span>
          ) : (
            <Button
              variant="outline"
              size="sm"
              onClick={() => window.api.checkForUpdates()}
              style={{ marginTop: 10 }}
            >
              Check for updates
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

(window as any).DocSettings = DocSettings;
(window as any).Library = Library;
(window as any).Formatter = Formatter;
(window as any).Settings = Settings;
export { DocSettings, Library, Formatter, Settings, EditHistory, HistoryButton };
